import React from 'react';

import { GroupUserSizePopover, GroupUserSizePopoverSkeleton } from '../components/GroupUserSizePopover';
import type { Group } from '../types';
import { useGetGroup } from './useGetGroup';

export interface UseGroupInfoContentOptions {
    /** The group object, which must contain a `uid`. */
    group: Group;
    /** Optional: A boolean to control the popover's visibility from a parent component. */
    isOpen?: boolean;
    /** Optional: A callback to notify the parent component when the popover's visibility changes. */
    setIsOpen?: (isOpen: boolean) => void;
}

/**
 * A React hook that provides the content for a group information popover.
 * It fetches group data on-demand when the `isOpen` prop is true.
 *
 * @param {UseGroupInfoContentOptions} options
 * @returns An object containing the `GroupUserSizePopoverContent` component.
 */
export const useGroupInfoContent = ({ group: inputGroup, ...props }: UseGroupInfoContentOptions) => {
    const [isOpen, setIsOpen] = React.useState(props.isOpen || false);

    const { uid } = inputGroup;

    const { data: group, isLoading } = useGetGroup({ uid: uid!, fields: 'userCount' }, { enabled: isOpen && !!uid });

    React.useEffect(() => {
        setIsOpen(props.isOpen || false);
    }, [props.isOpen]);

    const GroupUserSizePopoverContent =
        isLoading || !group ? (
            <GroupUserSizePopoverSkeleton />
        ) : (
            <GroupUserSizePopover userCount={group.userCount?.value} />
        );

    return { GroupUserSizePopoverContent };
};
